import { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useOutletContext } from 'react-router-dom';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { AbsenceCalendarRow, AbsenceListItem, LeaveBalanceResponse } from '@ip/shared-types';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import type { AppShellContext } from '@/components/layout/AppShell';
import { HrTabs } from '@/components/hr/HrTabs';
import { ApprovalQueue } from '@/components/hr/ApprovalQueue';
import { ComplianceReviewQueue } from '@/components/hr/ComplianceReviewQueue';
import { TeamCalendarGrid } from '@/components/hr/TeamCalendarGrid';
import { useHrMe, useHolidays } from '@/components/hr/useHrData';
import { useHrApi, SessionExpiredError } from '@/services/api';
import {
  ABSENCE_TYPE_META, addMonths, formatMonthYear, monthEnd, monthStart, todayYmd,
} from '@/lib/hr';
import { t } from '@/i18n';

/**
 * Manager surface (Task HR4 surface 2): the approval queue (with balance
 * after approval + team-overlap warnings) and the people × days team grid.
 */
export function HrTeamPage() {
  const { nip } = useOutletContext<AppShellContext>();
  const { hrMe, loading: meLoading } = useHrMe(nip);
  const { listAbsences, absenceCalendar, leaveBalance, approveAbsence, rejectAbsence } = useHrApi();

  const today = todayYmd();
  const [anchor, setAnchor] = useState(() => monthStart(today));
  const [view, setView] = useState<'month' | 'quarter'>('month');
  const [dept, setDept] = useState('all');

  const from = monthStart(anchor);
  const to = view === 'month' ? monthEnd(anchor) : monthEnd(addMonths(anchor, 2));
  const holidays = useHolidays(nip, [Number(from.slice(0, 4)), Number(to.slice(0, 4))]);

  const [gridRows, setGridRows] = useState<AbsenceCalendarRow[]>([]);
  const [gridLoading, setGridLoading] = useState(true);
  const [pending, setPending] = useState<AbsenceListItem[]>([]);
  const [queueLoading, setQueueLoading] = useState(true);
  const [conflictRows, setConflictRows] = useState<AbsenceCalendarRow[]>([]);
  const [balances, setBalances] = useState<Record<string, LeaveBalanceResponse | null>>({});
  const [busyId, setBusyId] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const selfId = hrMe?.employee?.id ?? null;
  const isManager = !!hrMe?.isManager;

  // Team grid for the visible range.
  useEffect(() => {
    if (!isManager) return;
    let mounted = true;
    setGridLoading(true);
    absenceCalendar(nip, from, to, 'team')
      .then((rows) => { if (mounted) setGridRows(rows); })
      .catch((error) => {
        if (!(error instanceof SessionExpiredError)) toast.error(t('hr.common.loadFailed'));
      })
      .finally(() => { if (mounted) setGridLoading(false); });
    return () => { mounted = false; };
  }, [nip, from, to, isManager, refreshKey, absenceCalendar]);

  // Approval queue + supporting data (conflict window, balances).
  useEffect(() => {
    if (!isManager) return;
    let mounted = true;
    setQueueLoading(true);
    (async () => {
      try {
        const all = await listAbsences(nip, { status: 'pending' });
        const queue = all
          .filter((a) => a.employeeId !== selfId)
          .sort((a, b) => (a.dateFrom < b.dateFrom ? -1 : 1));
        if (!mounted) return;
        setPending(queue);

        if (queue.length > 0) {
          const minFrom = queue.reduce((m, a) => (a.dateFrom < m ? a.dateFrom : m), queue[0].dateFrom);
          const maxTo = queue.reduce((m, a) => (a.dateTo > m ? a.dateTo : m), queue[0].dateTo);
          absenceCalendar(nip, minFrom, maxTo, 'team')
            .then((rows) => { if (mounted) setConflictRows(rows); })
            .catch(() => { /* conflicts are an enhancement, not core data */ });

          const poolEmployees = [...new Map(
            queue
              .filter((a) => ABSENCE_TYPE_META[a.type].pool)
              .map((a) => [a.employeeId, Number(a.dateFrom.slice(0, 4))]),
          ).entries()];
          const results = await Promise.allSettled(
            poolEmployees.map(([employeeId, year]) => leaveBalance(nip, employeeId, year)),
          );
          if (!mounted) return;
          const map: Record<string, LeaveBalanceResponse | null> = {};
          poolEmployees.forEach(([employeeId], i) => {
            const r = results[i];
            map[employeeId] = r.status === 'fulfilled' ? r.value : null;
          });
          setBalances(map);
        } else {
          setConflictRows([]);
          setBalances({});
        }
      } catch (error) {
        if (!(error instanceof SessionExpiredError)) toast.error(t('hr.common.loadFailed'));
      } finally {
        if (mounted) setQueueLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [nip, selfId, isManager, refreshKey, listAbsences, absenceCalendar, leaveBalance]);

  const norms = useMemo(() => {
    const map: Record<string, number> = {};
    for (const a of pending) {
      if (a.days > 0) map[a.employeeId] = Math.round((a.hoursTotal / a.days) * 100) / 100;
    }
    return map;
  }, [pending]);

  const departments = useMemo(
    () => [...new Set(gridRows.map((r) => r.department).filter((d): d is string => !!d))].sort(),
    [gridRows],
  );
  const filteredRows = useMemo(
    () => (dept === 'all' ? gridRows : gridRows.filter((r) => r.department === dept)),
    [gridRows, dept],
  );

  const handleDecide = useCallback(
    async (item: AbsenceListItem, decision: 'approve' | 'reject', note?: string) => {
      setBusyId(item.id);
      // Optimistic: drop the card, restore on failure.
      setPending((items) => items.filter((i) => i.id !== item.id));
      try {
        if (decision === 'approve') await approveAbsence(nip, item.id, note);
        else await rejectAbsence(nip, item.id, note);
        toast.success(decision === 'approve' ? t('hr.team.approved') : t('hr.team.rejected'), {
          description: item.employeeName,
        });
        setRefreshKey((k) => k + 1);
      } catch (error) {
        setPending((items) => [...items, item].sort((a, b) => (a.dateFrom < b.dateFrom ? -1 : 1)));
        if (!(error instanceof SessionExpiredError)) {
          toast.error(t('hr.team.actionFailed'), {
            description: error instanceof Error ? error.message : String(error),
          });
        }
      } finally {
        setBusyId(null);
      }
    },
    [nip, approveAbsence, rejectAbsence],
  );

  if (meLoading) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-9 w-64" />
        <Skeleton className="h-52 w-full" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  if (hrMe && !isManager) return <Navigate to={`/${nip}/hr`} replace />;

  const teamSize = hrMe?.directReportsCount ?? 0;

  return (
    <div className="flex flex-col gap-4">
      <HrTabs nip={nip} hrMe={hrMe} />

      {queueLoading ? (
        <Skeleton className="h-40 w-full" />
      ) : (
        <ApprovalQueue
          items={pending}
          balances={balances}
          norms={norms}
          teamRows={conflictRows}
          teamSize={teamSize}
          busyId={busyId}
          onDecide={handleDecide}
        />
      )}

      <ComplianceReviewQueue nip={nip} />

      <Card>
        <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 space-y-0">
          <CardTitle className="text-base capitalize">
            {t('hr.team.calendarTitle')} · {formatMonthYear(anchor)}
          </CardTitle>
          <div className="flex flex-wrap items-center gap-2">
            {departments.length > 0 && (
              <Select value={dept} onValueChange={setDept}>
                <SelectTrigger className="h-8 w-44" aria-label={t('hr.grid.department')}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('hr.grid.allDepartments')}</SelectItem>
                  {departments.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            <ToggleGroup
              type="single"
              variant="outline"
              size="sm"
              value={view}
              onValueChange={(v) => v && setView(v as 'month' | 'quarter')}
            >
              <ToggleGroupItem value="month">{t('hr.grid.month')}</ToggleGroupItem>
              <ToggleGroupItem value="quarter">{t('hr.grid.quarter')}</ToggleGroupItem>
            </ToggleGroup>
            <div className="flex items-center gap-1">
              <Button variant="outline" size="icon" className="size-8" aria-label={t('hr.calendar.prevMonth')}
                onClick={() => setAnchor(addMonths(anchor, -1))}>
                <ChevronLeft className="size-4" />
              </Button>
              <Button variant="outline" size="sm" className="h-8" onClick={() => setAnchor(monthStart(today))}>
                {t('hr.calendar.today')}
              </Button>
              <Button variant="outline" size="icon" className="size-8" aria-label={t('hr.calendar.nextMonth')}
                onClick={() => setAnchor(addMonths(anchor, 1))}>
                <ChevronRight className="size-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <TeamCalendarGrid
            rows={filteredRows}
            from={from}
            to={to}
            holidays={holidays}
            isHr={!!hrMe?.isHrAdmin}
            loading={gridLoading}
          />
        </CardContent>
      </Card>
    </div>
  );
}
